import type { CSSObject } from 'styled-components';
import styled from 'styled-components';

import { style } from '@genially/design-system';

interface ExpandedProps {
  $isExpanded: boolean;
}

interface BlurredProps {
  $isBlurred: boolean;
}

/**
 * Blurred and faded rather than hidden: the host can see there is something behind the
 * veil, and it takes a deliberate gesture to put names in front of the room. The heading
 * and its filter wear it too — a filter over names nobody can read is only a tease.
 */
const veil = ($isBlurred: boolean): CSSObject =>
  $isBlurred
    ? { filter: 'blur(6px)', opacity: 0.3, pointerEvents: 'none', userSelect: 'none' }
    : {};

export const Wrapper = styled.div<ExpandedProps>(({ theme: { size }, $isExpanded }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: size.units(1),

  // Only the full-screen column is bounded by the stage. In the side panel the section
  // keeps its natural height so the list rides the panel's own scroll.
  ...($isExpanded ? { minHeight: 0 } : {}),
}));

export const Header = styled.div<BlurredProps>(
  ({ theme: { size, transition }, $isBlurred }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: size.units(2),
    transition: transition.standard('filter', 'opacity'),
    ...veil($isBlurred),
  }),
);

export const List = styled.div<BlurredProps>(
  ({ theme: { size, transition }, $isBlurred }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: size.units(1),
    transition: transition.standard('filter', 'opacity'),
    ...veil($isBlurred),
  }),
);

export const ScrollArea = styled.div<ExpandedProps>(({ theme, $isExpanded }) =>
  $isExpanded
    ? { minHeight: 0, overflowY: 'auto', ...style.mixins.scrollbar(theme, true) }
    : {},
);
